/**
 * AI CONTEXT
 * Purpose: the four-corner perspective crop editor (JARVIS-391) — the same
 *   tool the mobile app ships, for staff reviewing iPad scans. Drag the four
 *   dots onto the corners of the artwork, Apply to flatten the marked quad,
 *   Save to persist it. Deliberately manual: no corner auto-detect (owner
 *   ask, Oct 2026), which is still JARVIS-129's to land.
 * Writes: none. The component produces JPEG blobs plus the rotation and
 *   calls `onSave`; the surface owns the Storage upload and the Firestore
 *   write, same split as rotation and hide on Quality Check.
 * Rotation: a plain number applied in CSS, never baked into stored pixels.
 *   The corner quad is held in IMAGE coordinates, so rotating only changes
 *   how it projects onto the stage — the dots stay stuck to the same
 *   physical corners of the artwork and re-place themselves, and the crop
 *   still comes out in the original's frame. Rotate is therefore live at
 *   any point, including mid-crop, and Apply leaves the angle alone.
 * Referenced by: ArtworkEditableThumb, which draws this editor inside the
 *   lightbox. `embedded` skips the second modal so crop is not a separate
 *   screen and is not done on the small tile.
 */
import { RotateLeftOutlined, RotateRightOutlined } from "@ant-design/icons"
import { Alert, Button, Modal, Space, Tooltip, Typography } from "antd"
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import styled from "styled-components"

import { FULLSCREEN_BG } from "#components/ArtworkTiles/artworkTilePrimitives"
import { COLORS } from "#styles/colors"
import {
  CROP_EXPORT_MAX_DIM,
  CROP_THUMB_EXPORT_MAX_DIM,
} from "#utils/artworkCropHelpers"
import {
  CROP_THUMB_JPEG_QUALITY,
  type CropBlobs,
  loadArtworkBitmap,
} from "#utils/artworkCropUpload"
import {
  encodeBitmapToJpeg,
  WARP_JPEG_QUALITY,
  warpImageToBlob,
} from "#utils/imageWarp"
import {
  clampToImage,
  defaultImageQuad,
  fitDisplayRect,
  getQuarterTurns,
  getRotatedSize,
  planPerspectiveCrop,
  type QuadPoints,
  toRawImagePoint,
  toStagePoint,
} from "#utils/perspectiveCrop"

const { Text } = Typography

/** 20px dot inside a 44px transparent target, so the grab area is generous. */
const HANDLE_SIZE = 20
const HANDLE_HIT_SIZE = 44
/** Half a target, so a dot riding the image edge renders whole. */
const STAGE_PADDING = HANDLE_HIT_SIZE / 2
/** Arrow-key nudge in image pixels, and a bigger step while Shift is held. */
const NUDGE_PX = 1
const NUDGE_FAST_PX = 10

const CORNER_LABELS = [
  "top left",
  "top right",
  "bottom right",
  "bottom left",
] as const

const QUARTER_TURN = 90
const FULL_TURN = 360

const normalizeRotation = (degrees: number): number =>
  (((Math.round(degrees / QUARTER_TURN) * QUARTER_TURN) % FULL_TURN) +
    FULL_TURN) %
  FULL_TURN

const SEditor = styled.div<{ $embedded: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: ${(p) => (p.$embedded ? "100%" : "calc(100vh - 150px)")};
`

const SStage = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  touch-action: none;
`

const SStageMessage = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #fff;
`

const SToolbar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`

const SHandle = styled.div`
  position: absolute;
  width: ${HANDLE_HIT_SIZE}px;
  height: ${HANDLE_HIT_SIZE}px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  touch-action: none;

  &::after {
    content: "";
    width: ${HANDLE_SIZE}px;
    height: ${HANDLE_SIZE}px;
    border-radius: 50%;
    border: 3px solid ${COLORS.theme.colorPrimary};
    background: rgba(255, 255, 255, 0.35);
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.45);
  }

  &:active {
    cursor: grabbing;
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: -8px;
  }
`

export interface ArtworkCropResult {
  /** Null when the user only turned the piece and never applied a crop. */
  blobs: CropBlobs | null
  /** The angle to store, 0-270. Applied in CSS, never baked into pixels. */
  rotation: number
}

export interface ArtworkCropModalProps {
  open: boolean
  /** Storage object path of the ORIGINAL; the editor never opens a crop. */
  originalPath: string | null
  /** The piece's stored rotation in degrees; the editor opens showing it. */
  rotation: number
  caption: string
  /** True when a crop is already saved, which enables Restore original. */
  hasCrop: boolean
  /**
   * Draw the editor in the caller's modal instead of opening a second one.
   * The lightbox uses this so crop tools belong to that screen.
   */
  embedded?: boolean
  onClose: () => void
  onSave: (result: ArtworkCropResult) => Promise<void>
  onRevert: () => Promise<void>
}

const ArtworkCropModal = ({
  open,
  originalPath,
  rotation,
  caption,
  hasCrop,
  embedded = false,
  onClose,
  onSave,
  onRevert,
}: ArtworkCropModalProps) => {
  const editor =
    open && originalPath ? (
      <CropEditor
        key={originalPath}
        embedded={embedded}
        originalPath={originalPath}
        initialRotation={rotation}
        hasCrop={hasCrop}
        onClose={onClose}
        onSave={onSave}
        onRevert={onRevert}
      />
    ) : null

  if (embedded) return editor

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width="96vw"
      style={{ top: 12, maxWidth: "none", paddingBottom: 0 }}
      styles={{ body: { background: FULLSCREEN_BG, padding: 12 } }}
      title={`Crop — ${caption}`}
    >
      {/* Keyed on the path so switching pieces remounts: every open starts from
          fresh editing state, with no reset effects to get wrong. */}
      {editor}
    </Modal>
  )
}

const CropEditor = ({
  embedded,
  originalPath,
  initialRotation,
  hasCrop,
  onClose,
  onSave,
  onRevert,
}: {
  embedded: boolean
  originalPath: string
  initialRotation: number
  hasCrop: boolean
  onClose: () => void
  onSave: (result: ArtworkCropResult) => Promise<void>
  onRevert: () => Promise<void>
}) => {
  const [sourceBitmap, setSourceBitmap] = useState<ImageBitmap | null>(null)
  // Non-null once Apply has run: the flattened pixels, and the dirty flag.
  const [workingBitmap, setWorkingBitmap] = useState<ImageBitmap | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isBusy, setIsBusy] = useState(false)
  const [stageSize, setStageSize] = useState<{
    width: number
    height: number
  } | null>(null)
  // The angle the stage is showing and the one Save will store. Apply does
  // not touch it: the crop lands in the original's frame, so the same angle
  // still turns it the right way up.
  const [rotation, setRotation] = useState(() =>
    normalizeRotation(initialRotation)
  )
  /**
   * The marked corners in IMAGE pixels, or null while untouched. Image
   * space, not stage space, is what makes a corner survive a rotation and a
   * window resize without being recalculated.
   */
  const [imageQuad, setImageQuad] = useState<QuadPoints | null>(null)

  const stageRef = useRef<HTMLDivElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    let cancelled = false
    void loadArtworkBitmap(originalPath)
      .then((bitmap) => {
        if (cancelled) {
          bitmap.close()
          return
        }
        setSourceBitmap(bitmap)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setLoadError(
          error instanceof Error ? error.message : "Could not load the image."
        )
      })
    return () => {
      cancelled = true
    }
  }, [originalPath])

  // Bitmaps hold decoded pixels off-heap, which the GC will not reclaim.
  useEffect(
    () => () => {
      sourceBitmap?.close()
    },
    [sourceBitmap]
  )
  useEffect(
    () => () => {
      workingBitmap?.close()
    },
    [workingBitmap]
  )

  const image = workingBitmap ?? sourceBitmap

  const displayRect = useMemo(() => {
    if (!image || !stageSize) return null
    const displayed = getRotatedSize(image.width, image.height, rotation)
    return fitDisplayRect(
      displayed.width,
      displayed.height,
      stageSize.width,
      stageSize.height,
      STAGE_PADDING
    )
  }, [image, rotation, stageSize])

  const quad = useMemo(
    () =>
      image ? (imageQuad ?? defaultImageQuad(image.width, image.height)) : null,
    [image, imageQuad]
  )

  // Projecting on every render is what keeps the handles glued to the
  // artwork: a rotation or a resize changes only this step.
  const stageQuad = useMemo(() => {
    if (!quad || !image || !displayRect) return null
    return quad.map((point) =>
      toStagePoint(point, displayRect, image.width, image.height, rotation)
    ) as QuadPoints
  }, [displayRect, image, quad, rotation])

  // Paint the stage: the image turned the way every other surface shows it.
  // Rotation is affine, so 2D canvas covers it; only the crop needs the
  // projective pass.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !image || !displayRect) return
    const ratio = window.devicePixelRatio || 1
    canvas.width = Math.max(1, Math.round(displayRect.width * ratio))
    canvas.height = Math.max(1, Math.round(displayRect.height * ratio))
    const context = canvas.getContext("2d")
    if (!context) return
    context.setTransform(ratio, 0, 0, ratio, 0, 0)
    context.clearRect(0, 0, displayRect.width, displayRect.height)
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = "high"
    context.translate(displayRect.width / 2, displayRect.height / 2)
    context.rotate((getQuarterTurns(rotation) * Math.PI) / 2)
    // Past the rotate, the drawing axes are the image's own, so a quarter
    // turn means drawing into the swapped-back fitted size.
    const drawn = getRotatedSize(
      displayRect.width,
      displayRect.height,
      rotation
    )
    context.drawImage(
      image,
      -drawn.width / 2,
      -drawn.height / 2,
      drawn.width,
      drawn.height
    )
  }, [displayRect, image, rotation])

  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setStageSize((current) =>
        current && current.width === width && current.height === height
          ? current
          : { width, height }
      )
    })
    observer.observe(stage)
    return () => observer.disconnect()
  }, [])

  /** Takes a point already in IMAGE pixels. */
  const moveCorner = useCallback(
    (corner: number, point: { x: number; y: number }) => {
      if (!quad || !image) return
      const next = [...quad] as QuadPoints
      next[corner] = clampToImage(point, image.width, image.height)
      setImageQuad(next)
    },
    [image, quad]
  )

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      // Pointer capture keeps the drag alive past the handle's own bounds,
      // which matters because the dot is small and the stage is large.
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    []
  )

  const handlePointerMove = useCallback(
    (corner: number, event: ReactPointerEvent<HTMLDivElement>) => {
      if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
      const stage = stageRef.current
      if (!stage || !displayRect || !image) return
      const bounds = stage.getBoundingClientRect()
      moveCorner(
        corner,
        toRawImagePoint(
          { x: event.clientX - bounds.left, y: event.clientY - bounds.top },
          displayRect,
          image.width,
          image.height,
          rotation
        )
      )
    },
    [displayRect, image, moveCorner, rotation]
  )

  // Arrow keys nudge the focused corner, so the editor is usable without a
  // pointer and a near-perfect corner can be trimmed by a pixel. The step is
  // in image pixels, applied along the axes the user sees.
  const handleKeyDown = useCallback(
    (corner: number, event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!quad || !displayRect || !image) return
      const step = event.shiftKey ? NUDGE_FAST_PX : NUDGE_PX
      const deltas: Record<string, { x: number; y: number }> = {
        ArrowLeft: { x: -step, y: 0 },
        ArrowRight: { x: step, y: 0 },
        ArrowUp: { x: 0, y: -step },
        ArrowDown: { x: 0, y: step },
      }
      const delta = deltas[event.key]
      if (!delta) return
      event.preventDefault()
      const onStage = toStagePoint(
        quad[corner],
        displayRect,
        image.width,
        image.height,
        rotation
      )
      moveCorner(
        corner,
        toRawImagePoint(
          { x: onStage.x + delta.x, y: onStage.y + delta.y },
          displayRect,
          image.width,
          image.height,
          rotation
        )
      )
    },
    [displayRect, image, moveCorner, quad, rotation]
  )

  const handleReset = useCallback(() => {
    setWorkingBitmap(null)
    setImageQuad(null)
    setRotation(normalizeRotation(initialRotation))
    setErrorMessage(null)
  }, [initialRotation])

  const handleRotate = useCallback((direction: 1 | -1) => {
    setRotation((current) =>
      normalizeRotation(current + direction * QUARTER_TURN)
    )
    setErrorMessage(null)
  }, [])

  // The single pixel operation. With untouched corners it is a no-op pass
  // over the whole image; with dragged corners it is simultaneously the crop
  // AND the perspective correction.
  const handleApply = useCallback(async () => {
    if (!image || !quad || isBusy) return
    const plan = planPerspectiveCrop({
      quad,
      maxOutputEdge: CROP_EXPORT_MAX_DIM,
    })
    if (!plan) {
      setErrorMessage(
        "Those corners don't form a usable shape — adjust and try again."
      )
      return
    }
    setIsBusy(true)
    try {
      const warped = await warpImageToBlob(image, plan)
      setWorkingBitmap(await createImageBitmap(warped))
      // The applied pixels become the new source, so the corners reset to
      // its edges rather than pointing into the image they came from.
      setImageQuad(null)
      setErrorMessage(null)
    } catch (error: unknown) {
      setErrorMessage(
        error instanceof Error
          ? `Crop failed: ${error.message}`
          : "Crop failed — adjust the corners and try again."
      )
    } finally {
      setIsBusy(false)
    }
  }, [image, isBusy, quad])

  const handleSave = useCallback(async () => {
    if (isBusy) return
    setIsBusy(true)
    try {
      // Rotating without cropping is a legitimate save: the angle is just a
      // number, and the editor is a reasonable place to notice a sideways
      // scan. Only an applied crop produces blobs.
      const blobs = workingBitmap
        ? await Promise.all([
            encodeBitmapToJpeg(
              workingBitmap,
              CROP_EXPORT_MAX_DIM,
              WARP_JPEG_QUALITY
            ),
            encodeBitmapToJpeg(
              workingBitmap,
              CROP_THUMB_EXPORT_MAX_DIM,
              CROP_THUMB_JPEG_QUALITY
            ),
          ]).then(([crop, thumb]) => ({ crop, thumb }))
        : null
      await onSave({ blobs, rotation })
      onClose()
    } catch (error: unknown) {
      setErrorMessage(
        error instanceof Error
          ? `Could not save the crop: ${error.message}`
          : "Could not save the crop — check your connection and retry."
      )
    } finally {
      setIsBusy(false)
    }
  }, [isBusy, onClose, onSave, rotation, workingBitmap])

  const handleRevert = useCallback(async () => {
    if (isBusy) return
    setIsBusy(true)
    try {
      await onRevert()
      onClose()
    } catch (error: unknown) {
      setErrorMessage(
        error instanceof Error
          ? `Could not restore the original: ${error.message}`
          : "Could not restore the original — check your connection and retry."
      )
    } finally {
      setIsBusy(false)
    }
  }, [isBusy, onClose, onRevert])

  // Dragged-but-unapplied corners are not dirty (there is nothing to save
  // yet), but Reset should still snap them back to the image edges.
  const hasAppliedCrop = workingBitmap !== null
  const rotationChanged = rotation !== normalizeRotation(initialRotation)
  const canReset = hasAppliedCrop || imageQuad !== null || rotationChanged
  const canSave = hasAppliedCrop || rotationChanged

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load this image"
        description={loadError}
        data-testid="artwork-crop-error"
      />
    )
  }

  return (
    <SEditor
      $embedded={embedded}
      data-testid="artwork-crop-editor"
      data-rotation={rotation}
    >
      <SStage ref={stageRef}>
        {displayRect && stageQuad ? (
          <>
            <canvas
              ref={canvasRef}
              data-testid="artwork-crop-canvas"
              style={{
                position: "absolute",
                left: displayRect.x,
                top: displayRect.y,
                width: displayRect.width,
                height: displayRect.height,
              }}
            />
            <svg
              width="100%"
              height="100%"
              style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
            >
              <path
                d={`${stageQuad
                  .map(
                    (point, index) =>
                      `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`
                  )
                  .join(" ")} Z`}
                fill="none"
                stroke={COLORS.theme.colorPrimary}
                strokeWidth={2}
              />
            </svg>
            {stageQuad.map((point, index) => (
              <SHandle
                key={CORNER_LABELS[index]}
                role="button"
                tabIndex={0}
                aria-label={`Crop corner ${CORNER_LABELS[index]}`}
                data-testid={`artwork-crop-handle-${index}`}
                data-image-x={Math.round(quad?.[index].x ?? 0)}
                data-image-y={Math.round(quad?.[index].y ?? 0)}
                onPointerDown={handlePointerDown}
                onPointerMove={(event) => handlePointerMove(index, event)}
                onKeyDown={(event) => handleKeyDown(index, event)}
                style={{
                  left: point.x - HANDLE_HIT_SIZE / 2,
                  top: point.y - HANDLE_HIT_SIZE / 2,
                }}
              />
            ))}
          </>
        ) : (
          <SStageMessage>
            <Text style={{ color: "#fff" }}>Loading image…</Text>
          </SStageMessage>
        )}
      </SStage>

      {errorMessage ? (
        <Alert
          type="error"
          showIcon
          message={errorMessage}
          data-testid="artwork-crop-message"
        />
      ) : null}

      <SToolbar>
        <Text style={{ color: COLORS.GRAY3 }}>
          {hasAppliedCrop
            ? "This is what will be saved. Reset to start over."
            : "Drag the circles to the corners of the artwork, then Apply."}
        </Text>
        <Space>
          <Tooltip title="Rotate 90° left">
            <Button
              icon={<RotateLeftOutlined />}
              onClick={() => handleRotate(-1)}
              disabled={isBusy || !image}
              aria-label="Rotate 90 degrees left"
              data-testid="artwork-crop-rotate-left"
            />
          </Tooltip>
          <Tooltip title="Rotate 90° right">
            <Button
              icon={<RotateRightOutlined />}
              onClick={() => handleRotate(1)}
              disabled={isBusy || !image}
              aria-label="Rotate 90 degrees right"
              data-testid="artwork-crop-rotate-right"
            />
          </Tooltip>
          <Button onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          {hasCrop ? (
            <Button
              onClick={() => void handleRevert()}
              disabled={isBusy}
              data-testid="artwork-crop-revert"
            >
              Restore original
            </Button>
          ) : null}
          <Button
            onClick={handleReset}
            disabled={isBusy || !canReset}
            data-testid="artwork-crop-reset"
          >
            Reset
          </Button>
          <Button
            onClick={() => void handleApply()}
            disabled={isBusy || !image}
            data-testid="artwork-crop-apply"
          >
            Apply
          </Button>
          <Button
            type="primary"
            loading={isBusy}
            onClick={() => void handleSave()}
            disabled={isBusy || !canSave}
            data-testid="artwork-crop-save"
          >
            Save
          </Button>
        </Space>
      </SToolbar>
    </SEditor>
  )
}

export default ArtworkCropModal
